/**
 * Режим выделения для замечаний контроля качества — как в WhatsApp: кружки слева у сообщений и
 * звонков ленты (только в режиме), клик по пустому месту строки отмечает, ссылки и плеер работают
 * как обычно. Вход — «Замечание» в меню «⋯» справа от сообщения или звонка, режим «Замечание» под
 * полем ответа, на телефоне — долгое нажатие. Под объектом — бейдж замечаний с их статусом; клик
 * открывает вкладку «Замечания» панели.
 * Спека `docs/specs/2026-10-25-qc-remarks-design.md`.
 *
 * То же выделение — «пачкой» (`purpose: "pick"`, любому с «Чатами»): отмечают несколько сообщений,
 * внизу вместо поля ответа — плашка «что сделать с отмеченными» (переслать в салон, скопировать,
 * замечание). Вход — значок «Выбрать» в шапке чата, «Выбрать несколько» в меню «⋯», долгое
 * нажатие на телефоне. Звонки в пачку не отмечаются.
 */
import { useQuery } from "@tanstack/react-query";
import { Check, ClipboardCheck } from "lucide-react";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from "react";

import { MessageMenu } from "@/components/chat/MessageMenu";
import { CLIENT_CHATS, KNOWLEDGE_EDIT, QC_REMARK, useCan } from "@/lib/access";
import { chatRemarksKey, inProgress, loadChatRemarks, needsAttention, type QcRef, type QcRemark, refKey } from "@/lib/qc";

type Qc = {
  chatId: number;
  /** Видит замечания (контролёр или редактор базы знаний). */
  see: boolean;
  /** Может оставлять замечания. */
  add: boolean;
  /** Может выделять сообщения пачкой (право «Чатов»). */
  pickable: boolean;
  active: boolean;
  /** Зачем выделение: замечание или действия с пачкой сообщений. */
  purpose: "remark" | "pick";
  picked: Map<string, QcRef>;
  remarks: QcRemark[];
  byRef: Map<string, QcRemark[]>;
  toggle: (r: QcRef) => void;
  /** Режим замечания (с отметками, что уже есть). */
  start: (r?: QcRef) => void;
  /** Режим пачки. */
  pick: (r?: QcRef) => void;
  stop: () => void;
  /** Поле замечания занято (голос или сохранение уходят) — выйти из режима нельзя, иначе
   * завершение старой отправки сбросит отметки и черновик нового замечания. */
  busy: boolean;
  setBusy: (v: boolean) => void;
  clear: () => void;
  /** Открыть панель «Замечания» (с замечанием в фокусе). */
  open: (remarkId?: number) => void;
};

const Ctx = createContext<Qc | null>(null);
export const useQc = () => useContext(Ctx);

export function QcProvider({ chatId, onOpen, children }: { chatId: number; onOpen: (remarkId?: number) => void; children: ReactNode }) {
  const chats = useCan(CLIENT_CHATS);
  const remarker = useCan(QC_REMARK);
  const editor = useCan(KNOWLEDGE_EDIT);
  const add = remarker && chats;
  const see = (remarker || editor) && chats;
  const [active, setActive] = useState(false);
  const [purpose, setPurpose] = useState<"remark" | "pick">("remark");
  const [picked, setPicked] = useState<Map<string, QcRef>>(new Map());
  const [busy, setBusy] = useState(false);
  const busyNow = useRef(false);
  busyNow.current = busy;
  const q = useQuery({ queryKey: chatRemarksKey(chatId), queryFn: () => loadChatRemarks(chatId), enabled: see, refetchInterval: 20_000 });
  const remarks = useMemo(() => q.data?.remarks ?? [], [q.data]);
  const byRef = useMemo(() => {
    const m = new Map<string, QcRemark[]>();
    for (const r of remarks) for (const ref of r.refs) m.set(refKey(ref), [...(m.get(refKey(ref)) ?? []), r]);
    return m;
  }, [remarks]);
  const toggle = useCallback((r: QcRef) => {
    setPicked((had) => {
      const next = new Map(had);
      if (next.has(refKey(r))) next.delete(refKey(r));
      else next.set(refKey(r), r);
      return next;
    });
  }, []);
  const start = useCallback(
    (r?: QcRef) => {
      if (!add) return;
      setActive(true);
      setPurpose("remark");
      if (r) setPicked((had) => new Map(had).set(refKey(r), r));
    },
    [add],
  );
  const pick = useCallback(
    (r?: QcRef) => {
      if (!chats || busyNow.current) return;
      setActive(true);
      setPurpose("pick");
      setPicked(r ? new Map([[refKey(r), r]]) : new Map());
    },
    [chats],
  );
  const stop = useCallback(() => {
    if (busyNow.current) return;
    setActive(false);
    setPicked(new Map());
  }, []);
  const clear = useCallback(() => setPicked(new Map()), []);
  const value = useMemo(
    () => ({
      chatId,
      see,
      add,
      pickable: chats,
      active,
      purpose,
      picked,
      remarks,
      byRef,
      toggle,
      start,
      pick,
      stop,
      clear,
      busy,
      setBusy,
      open: onOpen,
    }),
    [chatId, see, add, chats, active, purpose, picked, remarks, byRef, toggle, start, pick, stop, clear, busy, onOpen],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

const INTERACTIVE = "a,button,audio,video,input,textarea,select,summary,[role=button]";

/**
 * Строка ленты с кружком выделения слева (в режиме) и бейджем замечаний снизу. `anchor` — якорь
 * для перехода из панели (у сообщения он уже есть в пузыре — `msg-<id>`). `menu` — своё меню «⋯»
 * справа (у звонка; у сообщения меню рисует пузырь рядом с «Переслать» и «Ответить»).
 */
export function QcRow({ r, out, anchor, menu, children }: { r: QcRef | null; out: boolean; anchor?: string; menu?: boolean; children: ReactNode }) {
  const qc = useQc();
  const press = useRef<number | null>(null);
  if (!qc || !r || !(qc.see || qc.pickable)) return <>{children}</>;
  const key = refKey(r);
  const on = qc.picked.has(key);
  const marks = qc.see ? (qc.byRef.get(key) ?? []) : [];
  // В пачку — только сообщения: звонок в салон не перешлёшь.
  const selecting = qc.active && (qc.purpose === "remark" || r.kind === "message");
  const cancelPress = () => {
    if (press.current) window.clearTimeout(press.current);
    press.current = null;
  };
  return (
    <div
      id={anchor}
      className={`group/qc relative flex w-full flex-none items-start gap-1 rounded-[12px] ${selecting ? "cursor-pointer" : ""} ${on ? "bg-[var(--tg-primary)]/10" : ""}`}
      onClickCapture={(e) => {
        if (!selecting || (e.target as HTMLElement).closest(INTERACTIVE)) return;
        e.preventDefault();
        e.stopPropagation();
        qc.toggle(r);
      }}
      onDoubleClickCapture={(e) => {
        // В режиме выделения двойной клик не «ответить»: только отметка.
        if (qc.active) e.stopPropagation();
      }}
      onPointerDown={(e) => {
        // Долгое нажатие: у сообщения — пачка (замечание — одна из кнопок плашки), у звонка — замечание.
        const message = r.kind === "message" && qc.pickable;
        if (e.pointerType !== "touch" || qc.active || !(message || qc.add)) return;
        press.current = window.setTimeout(() => {
          press.current = null;
          navigator.vibrate?.(20);
          if (message) qc.pick(r);
          else qc.start(r);
        }, 550);
      }}
      onPointerUp={cancelPress}
      onPointerCancel={cancelPress}
      onPointerMove={cancelPress}
      data-annot="строка ленты с замечаниями"
    >
      {qc.active && (
        <div className="flex w-8 flex-none justify-center self-center">
          {selecting && (
            <button
              type="button"
              onClick={() => qc.toggle(r)}
              aria-pressed={on}
              title={on ? "Снять отметку" : qc.purpose === "pick" ? "Отметить сообщение" : "Отметить для замечания"}
              className="grid size-8 place-items-center"
            >
              <span
                className={`grid size-[22px] place-items-center rounded-full border-2 ${on ? "border-[var(--tg-primary)] bg-[var(--tg-primary)] text-white" : "border-[var(--tg-sec)]/60 bg-[var(--tg-surface)]"}`}
              >
                {on && <Check size={14} strokeWidth={3} />}
              </span>
            </button>
          )}
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        {children}
        {marks.length > 0 && <RemarkBadge marks={marks} out={out} onOpen={qc.open} />}
      </div>
      {menu && qc.add && !qc.active && (
        <div className="absolute right-0 top-1 transition-opacity focus-within:opacity-100 group-hover/qc:opacity-100 [@media(hover:hover)]:opacity-0">
          <MessageMenu
            items={[remarkItem(() => qc.start(r))]}
            className="grid size-8 place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)] hover:text-[var(--tg-primary)]"
          />
        </div>
      )}
    </div>
  );
}

/** Пункт «Замечание» меню «⋯»: включает выделение с этим объектом отмеченным. */
export const remarkItem = (onClick: () => void) => ({
  label: "Замечание",
  icon: <ClipboardCheck size={17} />,
  hint: "Замечание контроля качества — к нему можно отметить ещё сообщения и звонки",
  onClick,
  annot: "оставить замечание",
});

const MARK_COLOR = { good: "#2e9e4f", bad: "#e53935", note: "var(--tg-primary)" } as const;

/** Бейдж под объектом: число замечаний, статус последнего, «требует внимания» — если хоть одно. */
export function RemarkBadge({ marks, out, onOpen }: { marks: QcRemark[]; out: boolean; onOpen: (id?: number) => void }) {
  // Первым — то, что требует внимания (зависло, ошибка), иначе — последнее.
  const last = marks.find(needsAttention) ?? marks[0]!;
  const attention = marks.some(needsAttention);
  const open = marks.some(inProgress);
  return (
    <button
      type="button"
      onClick={() => onOpen(last.id)}
      title={marks.map((m) => `№${m.id} · ${m.author} · ${m.status_text}: ${(m.text || m.transcript).slice(0, 80)}`).join("\n")}
      className={`mb-1 mt-0.5 flex max-w-full items-center gap-1.5 rounded-full bg-[var(--tg-surface)] px-2 py-0.5 text-[12px] leading-[16px] shadow-[var(--tg-shadow)] hover:bg-[var(--tg-hover)] ${out ? "self-end" : "self-start"}`}
      data-annot="бейдж замечания"
    >
      <span className="size-2 flex-none rounded-full" style={{ background: MARK_COLOR[last.mark] }} />
      <span className="truncate">
        {marks.length > 1 ? `Замечаний ${marks.length} · ` : "Замечание · "}
        {last.status_text}
      </span>
      {attention && <span className="font-medium text-[#e53935]">!</span>}
      {!open && !attention && <Check size={12} className="flex-none text-[#2e9e4f]" />}
    </button>
  );
}

/** Кнопка в шапке группы (в чате клиента замечания — вкладкой панели): «Замечания N · требуют внимания K». */
export function QcHeaderButton() {
  const qc = useQc();
  if (!qc?.see || (qc.remarks.length === 0 && !qc.add)) return null;
  const attention = qc.remarks.filter(needsAttention).length;
  return (
    <button
      type="button"
      onClick={() => qc.open(qc.remarks.find(needsAttention)?.id)}
      title={
        qc.remarks.length
          ? `Замечания контроля качества по этому клиенту: ${qc.remarks.length}${attention ? `, требуют внимания ${attention}` : ""}`
          : "Замечаний по этому клиенту нет — отметьте сообщения значком слева"
      }
      className="relative grid size-10 flex-none place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]"
      data-annot="замечания в шапке"
    >
      <ClipboardCheck size={21} />
      {qc.remarks.length > 0 && (
        <span className="absolute right-0.5 top-0.5 min-w-[18px] rounded-full bg-[var(--tg-primary)] px-1 text-center text-[11px] font-medium leading-[18px] text-white">
          {qc.remarks.length}
        </span>
      )}
      {attention > 0 && (
        <span className="absolute bottom-0.5 right-0.5 min-w-[16px] rounded-full bg-[#e53935] px-1 text-center text-[10px] font-medium leading-[16px] text-white">
          !{attention}
        </span>
      )}
    </button>
  );
}
